<script setup lang="ts">
import type { TabListPassThroughOptions, TabsPassThroughOptions } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import type { useLink } from 'vue-router';
import { TabList as PTabList } from 'primevue';
import { computed, ref } from 'vue';
import {
  useRoute,
} from 'vue-router';

import VHorizontalScrollNav from '@components/scroll/VHorizontalScrollNav/VHorizontalScrollNav.vue';
import { useAddMerchantIdToRoute } from '@modules/merchant/composables';
import { useNamespaceRoutes } from '@modules/permissions/composables';
import { watchImmediate } from '@vueuse/core';

import type {
  VRouterTab,
  VRouterTabsEmits,
  VRouterTabsProps,
} from './types';
import VRouterTabItem from '../VRouterTabItem/VRouterTabItem.vue';

const componentProps = withDefaults(defineProps<VRouterTabsProps>(), {});
const emit = defineEmits<VRouterTabsEmits>();

const route = useRoute();
const { mixMerchantIdIntoRoute } = useAddMerchantIdToRoute();
const { isCurrentRouteChildOf } = useNamespaceRoutes();
const activeIndex = ref<number>(0);
const initialized = ref(false);

const tabsPt: TabsPassThroughOptions = {
  root: 'overflow-visible',
};

const tabsListPt: TabListPassThroughOptions = {
  activeBar: 'hidden',
};

const routeItems = computed(() => {
  return componentProps.items.map((item) => mixMerchantIdIntoRoute({ ...item, visible: item.visible ?? true })).filter((item) => item.visible);
});

function updateActiveIndex(): void {
  if (!routeItems.value)
    return;

  const newIndex = routeItems.value.findIndex((item) => {
    try {
      return isCurrentRouteChildOf(item.route);
    } catch {
      return false;
    }
  });

  // TODO: WORKAROUND - tabs fail navigation outside the state in some cases, possibly PrimeVue bug
  if (initialized.value && newIndex === -1) {
    return;
  }

  activeIndex.value = newIndex;
  initialized.value = true;
}

watchImmediate(() => [route.path, routeItems.value], updateActiveIndex, { deep: true });

function onClick(tab: MenuItem, event: MouseEvent, navigate?: ReturnType<typeof useLink>['navigate']): void {
  emit('click', tab as VRouterTab);

  if (navigate) {
    void navigate(event);
  }
}
</script>

<template>
  <VHorizontalScrollNav v-data-qa-auto-id="'VRouterTabs'"
    :active-index="activeIndex"
    element-class="p-tab"
    :disable-shadow-transition="disableShadowTransition"
  >
    <PTabs
      v-if="items?.length"
      v-model:value="activeIndex"
      :scrollable="false"
      :show-navigators="false"
      :pt="tabsPt"
    >
      <PTabList :pt="tabsListPt">
        <template
          v-for="(item, index) in routeItems"
          :key="item.label"
        >
          <router-link
            v-if="item.route"
            v-slot="{ navigate, href }"
            custom
            :to="item.route"
            :replace="replace"
          >
            <PTab
              :value="index"
              :disabled="item.disabled"
              @click="onClick(item, $event, navigate)"
            >
              <VRouterTabItem
                :href="href"
                :label="item.label"
              />
            </PTab>
          </router-link>
        </template>
      </PTabList>
    </PTabs>
  </VHorizontalScrollNav>
</template>
